import React, { ReactElement, useContext } from 'react';
import { GiveawayResponse, KeyFacts as KeyFactsDTO } from '@org-avp/avp-hck-dga-customerui-commons';
import { useTranslation } from 'react-i18next';
import {
  BreakpointWrapper,
  Breakpoints,
  Text,
  TokenTextAppearance,
  styled,
} from '@volkswagen-onehub/components-core';
import {
  Consumption as FuelType,
  Electricity as FuelTypeElectric,
  Engine as Power,
  TransmissionManual as Gear,
  Powertrain as Drive,
  ElectricCars as Range,
  Exterior as ExteriorColor,
  Seat as Upholstery,
  CarWheel as Rim,
  Contact as PreOwners,
  CountryRoad as Mileage,
  Performance as Acceleration,
  Delivery as MaxSpeed,
  Size as Dimensions,
  Calendar as InitialRegistration,
} from '@volkswagen-onehub/icons-core';
import { TFunction } from 'i18next';
import { minimalScreenWidth } from '@/common/breakpoints';
import { GiveawayContext } from '@/state/state';

export type KeyFact = {
  id: number;
  icon: ReactElement;
  title: string;
  data?: string;
  visible: boolean;
};

const StyledContainer = styled.div`
  padding-left: ${(props) => props.theme.size.dynamic0150};
  padding-right: ${(props) => props.theme.size.dynamic0150};
  padding-bottom: ${(props) => props.theme.size.dynamic0300};
  padding-top: ${(props) => props.theme.size.dynamic0300};
  background-image: ${(props) => props.theme.colors.gradient.left};

  @media ${minimalScreenWidth.l} {
    padding-left: ${(props) => props.theme.size.dynamic0250};
    padding-right: ${(props) => props.theme.size.dynamic0250};
  }

  @media ${minimalScreenWidth.xl} {
    padding-left: ${(props) => props.theme.size.dynamic0450};
    padding-right: ${(props) => props.theme.size.dynamic0450};
  }
`;

const StyledKeyFactsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;

  @media ${minimalScreenWidth.s} {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-row-gap: ${(props) => props.theme.size.dynamic0200};
    grid-column-gap: ${(props) => props.theme.size.dynamic0100};
  }

  @media ${minimalScreenWidth.m} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-row-gap: ${(props) => props.theme.size.dynamic0300};
    grid-column-gap: ${(props) => props.theme.size.dynamic0100};
  }
`;

const StyledKeyFact = styled.div`
  width: auto;
  text-align: left;

  @media ${minimalScreenWidth.s} {
    display: flex;
    align-items: center;
    padding-left: ${(props) => props.theme.size.static350};
    padding-right: ${(props) => props.theme.size.static350};
    padding-bottom: ${(props) => props.theme.size.static450};
  }

  @media screen and ${minimalScreenWidth.m} {
    flex-direction: column;
    text-align: center;
    padding: 0;

    h3 {
      padding-bottom: ${(props) => props.theme.size.static450};
    }
  }
`;

const ColoredIcon = styled.div`
  color: white;
  scale: 2;
  display: inline-block;

  @media screen and ${minimalScreenWidth.s} {
    padding-right: ${(props) => props.theme.size.dynamic0100};
  }

  @media screen and ${minimalScreenWidth.m} {
    padding-bottom: ${(props) => props.theme.size.dynamic0100};
    padding-right: 0;
  }
`;

const ColoredTitle = styled.div`
  padding-bottom: ${(props) => props.theme.size.dynamic0020};
  color: white;
`;

const ColoredDescription = styled.span`
  color: white;
`;

export const getKeyFactsDataGrid = (keyFacts: KeyFactsDTO, t: TFunction): KeyFact[] => {
  return [
    {
      id: 1,
      icon: <FuelType title="fuelType" variant="default" />,
      title: t('body.details.keyFacts.fuelType'),
      data: keyFacts.fuelType,
      visible: !keyFacts.range && !!keyFacts.fuelType,
    }, // if electric show "charging"
    {
      id: 2,
      icon: <FuelTypeElectric title="charging" variant="default" />,
      title: t('body.details.keyFacts.fuelType'),
      data: keyFacts.fuelType,
      visible: !!keyFacts.range && !!keyFacts.fuelType,
    },
    {
      id: 3,
      icon: <InitialRegistration title="initialReg" variant="default" />,
      title: t('body.details.keyFacts.initialReg'),
      data: keyFacts.initialReg,
      visible: !!keyFacts.initialReg,
    },
    {
      id: 4,
      icon: <Mileage title="mileage" variant="default" />,
      title: t('body.details.keyFacts.mileage'),
      data: keyFacts.mileage,
      visible: !!keyFacts.mileage,
    },
    {
      id: 5,
      icon: <PreOwners title="preOwners" variant="default" />,
      title: t('body.details.keyFacts.preOwners'),
      data: keyFacts.pOwners,
      visible: !!keyFacts.pOwners,
    },
    {
      id: 6,
      icon: <Power title="power" variant="default" />,
      title: t('body.details.keyFacts.power'),
      data: keyFacts.power,
      visible: !!keyFacts.power,
    },
    {
      id: 7,
      icon: <Gear title="transmission" variant="default" />,
      title: t('body.details.keyFacts.gear'),
      data: keyFacts.gear,
      visible: !!keyFacts.gear,
    },
    {
      id: 8,
      icon: <Drive title="drive" variant="default" />,
      title: t('body.details.keyFacts.drive'),
      data: keyFacts.drive,
      visible: !!keyFacts.drive,
    },
    {
      id: 9,
      icon: <Rim title="rim-type" variant="default" />,
      title: t('body.details.keyFacts.rim'),
      data: keyFacts.rimType?.split(',').slice(0, 4).join(','), // removes unnecessary rim data which is provided after the 4th string value
      visible: !!keyFacts.rimType,
    },
    {
      id: 10,
      icon: <Range title="electric-range" variant="default" />,
      title: t('body.details.keyFacts.range'),
      data: keyFacts.range,
      visible: !!keyFacts.range,
    },
    {
      id: 11,
      icon: <ExteriorColor title="exterior-color" variant="default" />,
      title: t('body.details.keyFacts.exteriorColor'),
      data: keyFacts.exteriorColor,
      visible: !!keyFacts.exteriorColor,
    },
    {
      id: 12,
      icon: <Upholstery title="upholstery" variant="default" />,
      title: t('body.details.keyFacts.upholstery'),
      data: keyFacts.upholstery,
      visible: !!keyFacts.upholstery,
    },
    {
      id: 13,
      icon: <Upholstery title="upholstery-color" variant="default" />,
      title: t('body.details.keyFacts.upholsteryColor'),
      data: keyFacts.upholsteryColor,
      visible: !!keyFacts.upholsteryColor,
    },
    {
      id: 14,
      icon: <Upholstery title="interior-design" variant="default" />,
      title: t('body.details.keyFacts.interiorDesign'),
      data: keyFacts.interiorDesign,
      visible: !!keyFacts.interiorDesign,
    },
    {
      id: 15,
      icon: <Acceleration title="acceleration" variant="default" />,
      title: t('body.details.keyFacts.acceleration'),
      data: keyFacts.acceleration,
      visible: !!keyFacts.acceleration,
    },
    {
      id: 16,
      icon: <MaxSpeed title="max speed" variant="default" />,
      title: t('body.details.keyFacts.maxSpeed'),
      data: keyFacts.maxSpeed,
      visible: !!keyFacts.maxSpeed,
    },
    {
      id: 17,
      icon: <Dimensions title="dimensions" variant="default" />,
      title: t('body.details.keyFacts.dimensions'),
      data: keyFacts.dimensions,
      visible: !!keyFacts.dimensions,
    },
  ];
};

export const KeyFacts = (): React.JSX.Element => {
  const giveawayResponse: GiveawayResponse = useContext(GiveawayContext);
  const { t } = useTranslation();
  const { keyFacts } = giveawayResponse;

  if (!keyFacts) {
    return <></>;
  }

  const keyFactsDataGrid: KeyFact[] = getKeyFactsDataGrid(keyFacts, t);

  return (
    <StyledContainer tabIndex={0}>
      <StyledKeyFactsContainer data-testid="keyfacts-container" role="list">
        {keyFactsDataGrid
          .filter((kf) => kf.visible)
          .sort((a, b) => a.id - b.id)
          .slice(0, 8)
          .map((keyFact) => (
            <StyledKeyFact key={keyFact.id} role="listitem">
              <ColoredIcon>{keyFact.icon}</ColoredIcon>
              <BreakpointWrapper max={Breakpoints.b560 + 1}>
                <div>
                  <Text bold appearance={TokenTextAppearance.label200}>
                    <ColoredTitle>{keyFact.title}</ColoredTitle>
                  </Text>
                  <Text appearance={TokenTextAppearance.label200}>
                    <ColoredDescription
                      data-testid={`keyfact-data-${keyFact.id}`}
                      dangerouslySetInnerHTML={{ __html: keyFact.data || '' }}
                    />
                  </Text>
                </div>
              </BreakpointWrapper>
              <BreakpointWrapper min={Breakpoints.b560}>
                <Text bold appearance={TokenTextAppearance.label200}>
                  <ColoredTitle>{keyFact.title}</ColoredTitle>
                </Text>
                <Text appearance={TokenTextAppearance.label200}>
                  <ColoredDescription
                    data-testid={`keyfact-data-${keyFact.id}`}
                    dangerouslySetInnerHTML={{ __html: keyFact.data || '' }}
                  />
                </Text>
              </BreakpointWrapper>
            </StyledKeyFact>
          ))}
      </StyledKeyFactsContainer>
    </StyledContainer>
  );
};
